import { useState, useEffect } from "react";
import clsx from "clsx";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { FormInput, FormTextarea, FormField } from "./FormFields";
import type {
  CollectionWithPois,
  CollectionVisibility,
} from "@poi-planner/shared";

interface CollectionEditorModalProps {
  open: boolean;
  onClose: () => void;
  collection?: CollectionWithPois | null;
  onSave: (data: {
    name: string;
    description?: string;
    visibility: CollectionVisibility;
  }) => Promise<void>;
}

const VISIBILITY_OPTIONS: {
  value: CollectionVisibility;
  label: string;
  icon: string;
  description: string;
}[] = [
  {
    value: "private",
    label: "Private",
    icon: "lock",
    description: "Only you can see this collection",
  },
  {
    value: "shared",
    label: "Shared",
    icon: "group",
    description: "Share via invite link",
  },
  {
    value: "public",
    label: "Public",
    icon: "public",
    description: "Anyone on this instance can view",
  },
];

export function CollectionEditorModal({
  open,
  onClose,
  collection,
  onSave,
}: CollectionEditorModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<CollectionVisibility>("private");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!collection;

  // Reset form when modal opens/changes
  useEffect(() => {
    if (open) {
      setError(null);
      if (collection) {
        setName(collection.name);
        setDescription(collection.description || "");
        setVisibility(collection.visibility);
      } else {
        setName("");
        setDescription("");
        setVisibility("private");
      }
    }
  }, [open, collection]);

  const handleSave = async () => {
    if (!name.trim() || saving) return;

    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        visibility,
      });
      onClose();
    } catch (err: unknown) {
      console.error("Failed to save collection:", err);
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Failed to save collection";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Collection" : "New Collection"}
      icon="folder_special"
      size="md"
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            variant="primary"
            loading={saving}
          >
            {saving ? "Saving..." : isEdit ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-error-bg border border-error/25 rounded-lg text-sm text-error">
            <span className="material-symbols-outlined text-lg">error</span>
            <span>{error}</span>
          </div>
        )}

        <FormField label="Name">
          <FormInput
            value={name}
            onChange={setName}
            placeholder="e.g., Paris Food Spots"
          />
        </FormField>

        <FormField label="Description (optional)">
          <FormTextarea
            value={description}
            onChange={setDescription}
            placeholder="What's this collection about?"
          />
        </FormField>

        <div>
          <label className="block text-xs font-semibold text-secondary uppercase tracking-wide mb-2">
            Visibility
          </label>
          <div className="flex flex-col gap-2">
            {VISIBILITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setVisibility(option.value)}
                className={clsx(
                  "flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left",
                  visibility === option.value
                    ? "border-primary bg-slate-50"
                    : "border-border bg-white hover:border-slate-300",
                )}
              >
                <div
                  className={clsx(
                    "flex items-center justify-center size-10 rounded-lg",
                    visibility === option.value
                      ? "bg-primary text-white"
                      : "bg-slate-100 text-secondary",
                  )}
                >
                  <span className="material-symbols-outlined">
                    {option.icon}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-primary">
                    {option.label}
                  </div>
                  <div className="text-xs text-tertiary">
                    {option.description}
                  </div>
                </div>
                {visibility === option.value && (
                  <span className="material-symbols-outlined text-primary">
                    check_circle
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
